import React, { useCallback, useEffect, useState, useRef } from 'react';
import { FaSave, FaSync, FaArrowLeft } from 'react-icons/fa';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import api from '../../../api/apiClient';

const LandingExtraContentEditor = ({ value, onChange, disabled }) => {
  const isUpdatingFromProps = useRef(false);
  
  const editor = useEditor(
    {
      extensions: [StarterKit],
      content: value || '',
      editable: !disabled,
      onUpdate({ editor }) {
        if (isUpdatingFromProps.current) return;
        const html = editor.getHTML();
        onChange(html);
      }
    },
    []
  );

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      isUpdatingFromProps.current = true;
      editor.commands.setContent(value || '', false);
      setTimeout(() => {
        isUpdatingFromProps.current = false;
      }, 0);
    }
  }, [editor, value]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  if (!editor) {
    return (
      <div className="border rounded p-2 bg-gray-50 text-gray-500 text-sm">Loading editor...</div>
    );
  }

  return (
    <div className="border rounded" style={{ minHeight: '160px' }}>
      <EditorContent editor={editor} className="p-2" />
    </div>
  );
};

const LandingPageManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [config, setConfig] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_cta_text: '',
    hero_cta_url: '',
    show_banners: true,
    show_features: true,
    show_awards: true,
    show_testimonials: true,
    show_faqs: true,
    extra_content_html: '',
    is_active: true
  });

  const loadConfig = useCallback(async () => {
    setError('');
    const response = await api.landingPage.getConfig();
    if (response.data?.success && response.data.data) {
      const c = response.data.data;
      setConfig({
        hero_title: c.hero_title || '',
        hero_subtitle: c.hero_subtitle || '',
        hero_cta_text: c.hero_cta_text || '',
        hero_cta_url: c.hero_cta_url || '',
        show_banners: !!c.show_banners,
        show_features: !!c.show_features,
        show_awards: !!c.show_awards,
        show_testimonials: !!c.show_testimonials,
        show_faqs: !!c.show_faqs,
        extra_content_html: c.extra_content_html || '',
        is_active: !!c.is_active
      });
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        await loadConfig();
      } catch (e) {
        setError('Failed to load landing page configuration');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [loadConfig]);

  const handleRefresh = useCallback(async () => {
    if (loading || refreshing) return;
    setRefreshing(true);
    setError('');
    setSuccess('');
    try {
      await loadConfig();
    } catch (e) {
      setError('Failed to refresh landing page configuration');
    } finally {
      setRefreshing(false);
    }
  }, [loadConfig, loading, refreshing]);

  const handleChange = useCallback((event) => {
    const { name, type, checked, value } = event.target;
    setConfig((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleExtraContentChange = useCallback((html) => {
    setConfig((prev) => ({
      ...prev,
      extra_content_html: html || ''
    }));
  }, []);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!config.hero_title || !config.hero_title.trim()) {
      setError('Hero title is required.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        hero_title: config.hero_title.trim(),
        hero_subtitle: config.hero_subtitle?.trim() || null,
        hero_cta_text: config.hero_cta_text?.trim() || null,
        hero_cta_url: config.hero_cta_url?.trim() || null,
        show_banners: !!config.show_banners,
        show_features: !!config.show_features,
        show_awards: !!config.show_awards,
        show_testimonials: !!config.show_testimonials,
        show_faqs: !!config.show_faqs,
        extra_content_html: config.extra_content_html || null,
        is_active: !!config.is_active
      };

      const response = await api.landingPage.saveConfig(payload);
      if (response.data?.success) {
        setSuccess('Landing page configuration saved successfully.');
        await loadConfig();
      } else {
        setError(response.data?.message || 'Failed to save configuration');
      }
    } catch (e) {
      setError('Failed to save landing page configuration');
    } finally {
      setSaving(false);
    }
  }, [config, loadConfig]);

  return (
    <div className="p-3 sm:p-6 md:p-8 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-3">
        <div>
          <h3 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Landing Page Builder</h3>
          <p className="text-sm text-gray-600 mt-1">Configure your public landing page hero and which site management blocks should be visible.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            {refreshing ? 'Refreshing...' : (<><FaSync className="inline-block mr-2"/> Refresh</>)}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-4">
        {error && (
          <div className="mb-3 p-3 rounded bg-red-50 border border-red-100 text-red-700">{error}</div>
        )}
        {success && (
          <div className="mb-3 p-3 rounded bg-emerald-50 border border-emerald-100 text-emerald-700">{success}</div>
        )}

        {loading ? (
          <div className="text-center py-6"><div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div></div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Hero title</label>
                <input type="text" name="hero_title" value={config.hero_title} onChange={handleChange} disabled={saving} placeholder="Main headline of your landing page" className="mt-1 w-full px-4 py-2 border border-purple-200 rounded-lg" />

                <label className="block text-sm font-medium text-gray-700 mt-4">Hero subtitle (optional)</label>
                <textarea name="hero_subtitle" rows={2} value={config.hero_subtitle} onChange={handleChange} disabled={saving} placeholder="Short supporting text under the main headline" className="mt-1 w-full px-4 py-2 border border-purple-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">CTA button text (optional)</label>
                <input type="text" name="hero_cta_text" value={config.hero_cta_text} onChange={handleChange} disabled={saving} placeholder="e.g. Get Started" className="mt-1 w-full px-4 py-2 border border-purple-200 rounded-lg" />

                <label className="block text-sm font-medium text-gray-700 mt-4">CTA target URL (optional)</label>
                <input type="text" name="hero_cta_url" value={config.hero_cta_url} onChange={handleChange} disabled={saving} placeholder="e.g. /signup or external URL" className="mt-1 w-full px-4 py-2 border border-purple-200 rounded-lg" />

                <div className="mt-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" name="is_active" checked={config.is_active} onChange={handleChange} disabled={saving} className="w-4 h-4" />
                    <span>Landing page configuration is active</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 border rounded-lg">
                <h6 className="font-semibold mb-3">Sections visibility</h6>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="show_banners" checked={config.show_banners} onChange={handleChange} disabled={saving} className="w-4 h-4" /> <span>Show banners section</span></label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="show_features" checked={config.show_features} onChange={handleChange} disabled={saving} className="w-4 h-4" /> <span>Show features section</span></label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="show_awards" checked={config.show_awards} onChange={handleChange} disabled={saving} className="w-4 h-4" /> <span>Show awards section</span></label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="show_testimonials" checked={config.show_testimonials} onChange={handleChange} disabled={saving} className="w-4 h-4" /> <span>Show testimonials section</span></label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="show_faqs" checked={config.show_faqs} onChange={handleChange} disabled={saving} className="w-4 h-4" /> <span>Show FAQs section</span></label>
                </div>
                <p className="text-xs text-gray-500 mt-3">These switches control whether each site management block appears on the public landing page.</p>
              </div>

              <div className="p-4 border rounded-lg">
                <h6 className="font-semibold mb-3">Additional content</h6>
                <LandingExtraContentEditor value={config.extra_content_html} onChange={handleExtraContentChange} disabled={saving} />
                <p className="text-xs text-gray-500 mt-3">Optional extra rich content (e.g. secondary hero, trust badges, layout notes) to render on your landing page.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
              <div className="lg:col-span-7 p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-500">Hero</div>
                    <div className="mt-2">
                      <strong className="text-base">{config.hero_title || 'No hero title set yet'}</strong>
                      {config.hero_subtitle && <div className="text-gray-500 text-sm mt-1">{config.hero_subtitle}</div>}
                    </div>
                    <div className="mt-3 text-sm text-gray-600">
                      <span className="text-gray-400 mr-1">CTA:</span>
                      {config.hero_cta_text ? (
                        <>
                          <span>{config.hero_cta_text}</span>
                          {config.hero_cta_url && <span className="text-gray-400"> ({config.hero_cta_url})</span>}
                        </>
                      ) : (
                        <span className="text-gray-400">No CTA configured</span>
                      )}
                    </div>
                    <div className="mt-4">
                      <div className="text-sm text-gray-500">Sections</div>
                      <div className="flex flex-wrap gap-2 mt-2 text-sm">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${config.show_banners ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>Banners</span>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${config.show_features ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>Features</span>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${config.show_awards ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>Awards</span>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${config.show_testimonials ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>Testimonials</span>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${config.show_faqs ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>FAQs</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${config.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>{config.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5 flex justify-end">
                <button type="submit" disabled={saving} className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg">
                  {saving ? 'Saving...' : (<><FaSave className="inline-block mr-2"/> Save configuration</>)}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LandingPageManager;
